import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { EntityContext } from '../entity';
import { toApiError } from '../utils/apiError';
import { signatureMethodOptions } from '../utils/signatureMethods';

export default function TemplateConfigureEnvelopePage() {
  const { templateKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    getGroups,
    getWebhooks,
    createXpressEnvelopeByTemplate,
    loading,
    signatureMethods,
  } = useContext(EntityContext);

  const getGroupsRef = useRef(getGroups);
  const getWebhooksRef = useRef(getWebhooks);

  const templatePayload = location.state?.templatePayload;
  const templateGeneratorState = location.state?.templateGeneratorState || null;

  const [envelopeName, setEnvelopeName] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [selectedWebhookKey, setSelectedWebhookKey] = useState('');
  const [groupsList, setGroupsList] = useState([]);
  const [webhookList, setWebhookList] = useState([]);
  const [selectedGroupKey, setSelectedGroupKey] = useState('');
  const [selectedSignatureType, setSelectedSignatureType] = useState('email');
  const [signers, setSigners] = useState([]);
  const [pageError, setPageError] = useState('');
  const [missingSigners, setMissingSigners] = useState([]);
  const [pageSuccess, setPageSuccess] = useState('');
  const [createdEnvelopeKey, setCreatedEnvelopeKey] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    getGroupsRef.current = getGroups;
  }, [getGroups]);

  useEffect(() => {
    getWebhooksRef.current = getWebhooks;
  }, [getWebhooks]);

  useEffect(() => {
    Promise.allSettled([getGroupsRef.current(), getWebhooksRef.current()]).then((results) => {
      const groupsResult = results[0];
      if (groupsResult.status === 'fulfilled') {
        setGroupsList(groupsResult.value?.groups || []);
      }

      const webhooksResult = results[1];
      if (webhooksResult.status === 'fulfilled') {
        setWebhookList(webhooksResult.value?.configurations || []);
      }
    });
  }, []);

  useEffect(() => {
    if (!pageSuccess) return undefined;

    setIsRedirecting(true);
    const timeoutId = setTimeout(() => {
      navigate('/dashboard/app');
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [navigate, pageSuccess]);

  const handleAddSigner = useCallback(() => {
    if (!selectedGroupKey) return;

    const alreadyAdded = signers.some((signer) => signer.group_key === selectedGroupKey);
    if (alreadyAdded) {
      setPageError('Este grupo já foi adicionado.');
      return;
    }

    const selectedGroup = groupsList.find((group) => group.group_key === selectedGroupKey);
    const nextSigner = {
      group_key: selectedGroupKey,
      signature_type: selectedSignatureType,
      group_name: selectedGroup?.group_name || selectedGroupKey,
    };

    setSigners((prev) => [...prev, nextSigner]);
    setSelectedGroupKey('');
    setSelectedSignatureType('email');
    setPageError('');
  }, [groupsList, selectedGroupKey, selectedSignatureType, signers]);

  const handleRemoveSigner = useCallback((groupKey) => {
    setSigners((prev) => prev.filter((signer) => signer.group_key !== groupKey));
  }, []);

  const handleSubmit = async () => {
    setPageError('');
    setMissingSigners([]);
    setPageSuccess('');
    setCreatedEnvelopeKey('');

    if (!templatePayload || !templatePayload.template_key || !templatePayload.document_name) {
      setPageError('Dados do template não encontrados. Volte para a pré-visualização.');
      return;
    }

    if (!envelopeName.trim()) {
      setPageError('Informe o nome do envelope.');
      return;
    }

    if (signers.length === 0) {
      setPageError('Adicione ao menos um grupo de assinantes.');
      return;
    }

    const payload = {
      envelope: {
        envelope_name: envelopeName.trim(),
        signers: signers.map((signer) => ({
          group_key: signer.group_key,
          signature_type: signer.signature_type,
        })),
        ...(selectedWebhookKey ? { webhook_configuration_key: selectedWebhookKey } : {}),
      },
      template: templatePayload,
      send_email: sendEmail,
    };

    try {
      const response = await createXpressEnvelopeByTemplate(payload);
      const envelopeKey = response?.envelope_key || response?.envelope?.envelope_key || '';
      setCreatedEnvelopeKey(envelopeKey);
      setPageSuccess('Envelope enviado com sucesso.');
    } catch (error) {
      const apiError = toApiError(error);
      setPageError(apiError.message);
      setMissingSigners(apiError.errors);
    }
  };

  if (!templatePayload) {
    return (
      <Container>
        <Stack spacing={2}>
          <Typography variant="h4">Concluir Envelope por Template</Typography>
          <Alert severity="warning">
            Não foi possível carregar os dados da pré-visualização. Volte para Templates e gere o documento novamente.
          </Alert>
          <Box>
            <Button variant="contained" onClick={() => navigate('/dashboard/templates')}>
              Voltar para Templates
            </Button>
          </Box>
        </Stack>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title> Concluir Envelope por Template </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Concluir Envelope por Template</Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate(`/dashboard/templates/${templateKey}/generate-document`, {
                state: { templateGeneratorState },
              })
            }
          >
            Voltar para pré-visualização
          </Button>
        </Stack>

        <Card sx={{ p: 3 }}>
          <Stack spacing={2}>
            <TextField
              label="Nome do envelope"
              value={envelopeName}
              onChange={(event) => setEnvelopeName(event.target.value)}
              placeholder="Ex.: Contrato Março/2026"
            />

            <TextField
              select
              label="Webhook (opcional)"
              value={selectedWebhookKey}
              onChange={(event) => setSelectedWebhookKey(event.target.value)}
            >
              <MenuItem value="">Nenhum</MenuItem>
              {webhookList.map((webhook) => (
                <MenuItem key={webhook.webhook_configuration_key} value={webhook.webhook_configuration_key}>
                  {webhook.url || webhook.webhook_configuration_key}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography>Enviar email para assinantes</Typography>
              <Switch checked={sendEmail} onChange={(event) => setSendEmail(event.target.checked)} />
            </Stack>

            <Divider />

            <Typography variant="h6">Grupos de assinantes</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                fullWidth
                label="Grupo"
                value={selectedGroupKey}
                onChange={(event) => setSelectedGroupKey(event.target.value)}
              >
                {groupsList.map((group) => (
                  <MenuItem key={group.group_key} value={group.group_key}>
                    {group.group_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Tipo de assinatura"
                value={selectedSignatureType}
                onChange={(event) => setSelectedSignatureType(event.target.value)}
              >
                {signatureMethodOptions(signatureMethods).map((signatureTypeOption) => (
                  <MenuItem key={signatureTypeOption.value} value={signatureTypeOption.value}>
                    {signatureTypeOption.label}
                  </MenuItem>
                ))}
              </TextField>

              <Button variant="outlined" onClick={handleAddSigner}>
                Adicionar grupo
              </Button>
            </Stack>

            <Stack spacing={1}>
              {signers.map((signer) => (
                <Card key={signer.group_key} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="subtitle2">{signer.group_name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tipo: {signer.signature_type}
                      </Typography>
                    </Box>
                    <IconButton color="error" onClick={() => handleRemoveSigner(signer.group_key)}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Card>
              ))}
            </Stack>

            <Divider />

            <Typography variant="subtitle2">Template que será enviado</Typography>
            <Typography variant="body2" color="text.secondary">
              Documento: {templatePayload.document_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Template key: {templatePayload.template_key}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Variáveis: {templatePayload.variables?.length || 0}
            </Typography>

            {pageError ? (
              <Alert severity="error">
                {pageError}
                {missingSigners.length > 0 ? (
                  <ul>
                    {missingSigners.map((missing, i) => (
                      <li key={`${missing.field}-${i}`}>{missing.message}</li>
                    ))}
                  </ul>
                ) : null}
              </Alert>
            ) : null}
            {pageSuccess ? (
              <Alert severity="success">
                {pageSuccess}
                {createdEnvelopeKey ? ` Chave do envelope: ${createdEnvelopeKey}` : ''}
                {isRedirecting ? ' Redirecionando para o dashboard...' : ''}
              </Alert>
            ) : null}

            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" onClick={handleSubmit} disabled={loading || isRedirecting}>
                {loading ? 'Enviando...' : 'Enviar envelope'}
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Container>
    </>
  );
}
